import { Vec3 } from '@/core/math/Vec3';
import { CelestialBody } from '@/universe/CelestialBody';
import { FlightComputer } from '../FlightComputer';
import { ManeuverPlanner } from '../ManeuverPlanner';
import { FlightEnvironment, Spacecraft } from '../Spacecraft';
import { TrajectoryPredictor } from '../TrajectoryPredictor';

export interface MacroContext {
  ship: Spacecraft;
  computer: FlightComputer;
  planner: ManeuverPlanner;
  predictor: TrajectoryPredictor;
  body: CelestialBody;
  env: FlightEnvironment;
  /** Simulation time, seconds. */
  t: number;
  /** Fixed step, seconds. */
  dt: number;
  /**
   * The selected navigation target, resolved fresh every step rather than
   * snapshotted — a macro that flies to another body has to follow where that
   * body actually is, not where it was when the target was picked.
   */
  target: CelestialBody | null;
  /**
   * Highest terrain on a body, metres, for impact tests along a predicted
   * route. Bodies with no streamed surface report zero.
   */
  terrainCeilingFor: (body: CelestialBody) => number;
}

/**
 * What a macro promises before the player commits to it. Every field is an
 * estimate derived from the vessel's actual current state, not a constant.
 */
export interface MacroEstimate {
  /** Expected wall-clock-equivalent simulation duration, seconds. */
  duration: number;
  /** Expected propellant use, kg. */
  propellant: number;
  /** Delta-v the manoeuvre requires, m/s. */
  deltaV: number;
  /** Highest acceleration the vessel will experience, m/s^2. */
  peakAcceleration: number;
  /** Expected peak hull heating rate, W/m^2. */
  heatLoad: number;
  /** Conditions that would cause the macro to abort. */
  failureModes: string[];
  /** True when the macro cannot be started at all right now. */
  blocked: boolean;
  blockedReason: string | null;
}

export type MacroStatus = 'idle' | 'running' | 'paused' | 'complete' | 'failed' | 'aborted';

export interface MacroSafety {
  /** 0 = fastest, 1 = most conservative. Trades time against margins. */
  margin: number;
  /** true = prioritise propellant efficiency over elapsed time. */
  preferEfficiency: boolean;
}

/**
 * A flight macro.
 *
 * Macros are sequences of *physical operations*. They command the same
 * actuators the player does, through the same flight computer, and they are
 * subject to the same fuel, power, thermal and structural limits. A macro
 * cannot teleport the vessel, cannot set its velocity, and cannot complete a
 * manoeuvre the vessel does not have the delta-v for; it will report a failure
 * instead.
 */
export interface Macro {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  /** Ordered phase names, for progress display. */
  readonly phases: readonly string[];
  /** Index into `phases`. */
  readonly phaseIndex: number;
  readonly status: MacroStatus;
  /** Human-readable explanation of the current phase or the failure. */
  readonly statusText: string;

  estimate(ctx: MacroContext): MacroEstimate;
  start(ctx: MacroContext): void;
  update(ctx: MacroContext): MacroStatus;
  abort(ctx: MacroContext): void;
}

/** Shared implementation details for the concrete macros. */
export abstract class BaseMacro implements Macro {
  abstract readonly id: string;
  abstract readonly name: string;
  abstract readonly description: string;
  abstract readonly phases: readonly string[];

  phaseIndex = 0;
  status: MacroStatus = 'idle';
  statusText = '';
  safety: MacroSafety = { margin: 0.35, preferEfficiency: false };

  protected startTime = 0;
  protected startFuel = 0;

  abstract estimate(ctx: MacroContext): MacroEstimate;
  protected abstract tick(ctx: MacroContext): MacroStatus;

  start(ctx: MacroContext): void {
    const est = this.estimate(ctx);
    if (est.blocked) {
      this.status = 'failed';
      this.statusText = est.blockedReason ?? 'Preconditions not met';
      return;
    }
    this.phaseIndex = 0;
    this.status = 'running';
    this.startTime = ctx.t;
    this.startFuel = ctx.ship.massProperties.fuel;
    this.onStart(ctx);
  }

  protected onStart(_ctx: MacroContext): void {
    // Optional hook.
  }

  update(ctx: MacroContext): MacroStatus {
    if (this.status !== 'running') return this.status;
    this.status = this.tick(ctx);
    return this.status;
  }

  abort(ctx: MacroContext): void {
    this.status = 'aborted';
    this.statusText = 'Aborted by pilot';
    this.releaseControls(ctx);
  }

  /** Hands the vessel back to the player in a safe configuration. */
  protected releaseControls(ctx: MacroContext): void {
    const c = ctx.computer;
    c.holds.altitudeHold = false;
    c.holds.verticalSpeedHold = false;
    c.holds.hoverAssist = false;
    c.holds.velocityHold = false;
    c.holds.matchTargetVelocity = false;
    c.setAlignment('none');
    ctx.ship.controls.throttle = 0;
    ctx.ship.controls.mainEngineArmed = false;
    ctx.ship.controls.atmosphericEnginesArmed = false;
    ctx.ship.controls.fieldDriveMode = 'off';
  }

  protected fail(ctx: MacroContext, reason: string): MacroStatus {
    this.statusText = reason;
    this.releaseControls(ctx);
    return 'failed';
  }

  protected advance(phase: number, text: string): void {
    this.phaseIndex = phase;
    this.statusText = text;
  }

  /** Elapsed simulation time since the macro started, seconds. */
  protected elapsed(ctx: MacroContext): number {
    return ctx.t - this.startTime;
  }

  /** Propellant consumed since the macro started, kg. */
  protected propellantUsed(ctx: MacroContext): number {
    return Math.max(0, this.startFuel - ctx.ship.massProperties.fuel);
  }

  /**
   * Common safety interlock shared by every macro: refuse to keep running if
   * the hull is failing, propellant is exhausted, or the power bus has
   * collapsed. Returns a failure reason, or null when everything is fine.
   */
  protected checkVitals(ctx: MacroContext): string | null {
    const ship = ctx.ship;
    if (ship.damage.hull >= 0.95) return 'Hull structural failure';
    if (ship.thermal.temperature > ship.thermal.temperatureLimit * 1.08) return 'Hull temperature exceeded limit';
    if (ship.power.chargeFraction <= 0.001 && ship.lastPower && ship.lastPower.generation <= 0) {
      return 'Electrical power exhausted';
    }
    return null;
  }
}

/** Direction of planetary rotation at the vessel, for prograde launches. */
export function eastwardDirection(env: FlightEnvironment): Vec3 {
  const up = env.up;
  let east = Vec3.crossVectors(new Vec3(0, 1, 0), up);
  if (east.lengthSq() < 1e-12) east = Vec3.anyPerpendicular(up);
  return east.normalize();
}
